/*
 * Scroll restoration fallback.
 *
 * Native history scroll restoration works on desktop, but fails on mobile:
 * back navigation often misses the bfcache, and by the time the browser tries
 * to restore scrollY the document is still short (lazy media, late sections),
 * so it clamps to the top and gives up.
 *
 * This stores the scroll position per URL in sessionStorage and re-applies it
 * on back/forward navigation, retrying until the document is tall enough.
 * It is purely additive: when the browser already restored the position
 * correctly (desktop), the first attempt sees the correct scrollY and bails.
 */
(function () {
  if (!('sessionStorage' in window) || !('performance' in window)) return;

  const STORAGE_KEY = 'printivart:scroll-positions';
  const MAX_ENTRIES = 30;
  const RESTORE_TIMEOUT = 3000;
  const SAVE_THROTTLE = 250;
  const TOLERANCE = 2;

  const positionKey = () => window.location.pathname + window.location.search;

  function readPositions() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writePositions(positions) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch (error) {
      // Private mode / quota exceeded — restoration is a nicety, so ignore.
    }
  }

  function savePosition() {
    const positions = readPositions();
    positions[positionKey()] = { y: Math.round(window.scrollY), t: Date.now() };

    const keys = Object.keys(positions);
    if (keys.length > MAX_ENTRIES) {
      keys
        .sort((a, b) => positions[a].t - positions[b].t)
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach((key) => delete positions[key]);
    }

    writePositions(positions);
  }

  let saveScheduled = false;
  function scheduleSave() {
    if (saveScheduled) return;
    saveScheduled = true;
    setTimeout(() => {
      saveScheduled = false;
      savePosition();
    }, SAVE_THROTTLE);
  }

  window.addEventListener('scroll', scheduleSave, { passive: true });
  window.addEventListener('pagehide', savePosition);
  document.addEventListener('visibilitychange', () => {
    // iOS Safari does not reliably fire pagehide when the tab is backgrounded.
    if (document.visibilityState === 'hidden') savePosition();
  });

  function isBackForwardNavigation() {
    const entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
    if (entries && entries.length) return entries[0].type === 'back_forward';
    return !!(performance.navigation && performance.navigation.type === 2);
  }

  function maxScrollY() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  let restoring = false;
  function restorePosition() {
    if (restoring || window.location.hash) return;

    const saved = readPositions()[positionKey()];
    if (!saved || saved.y < 1) return;
    if (Math.abs(window.scrollY - saved.y) <= TOLERANCE) return;

    restoring = true;
    const deadline = Date.now() + RESTORE_TIMEOUT;

    const stop = () => {
      restoring = false;
    };
    // A deliberate scroll from the visitor always wins over restoration.
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((eventName) => {
      window.addEventListener(eventName, stop, { passive: true, once: true });
    });

    (function attempt() {
      if (!restoring) return;

      if (maxScrollY() >= saved.y) {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) <= TOLERANCE) return stop();
      }

      if (Date.now() < deadline) {
        requestAnimationFrame(attempt);
        return;
      }

      // Timed out: the page never grew back to its previous height, so get as
      // close as we can rather than leaving the visitor at the top.
      window.scrollTo(0, Math.min(saved.y, maxScrollY()));
      stop();
    })();
  }

  if (isBackForwardNavigation()) restorePosition();

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || isBackForwardNavigation()) restorePosition();
  });
})();
